// PDF export — cover page, BOM table, compute/storage/network summaries, methodology footer.
// Uses jsPDF + jspdf-autotable. Triggers a browser download.

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { Project, SizingResult, SizingConfig, ComputeResult, StorageResult, NetworkResult } from '../../types';

// ─── Palette ──────────────────────────────────────────────────────────────────

const C = {
  navy:       [15, 23, 42]   as [number, number, number],
  blue:       [30, 64, 175]  as [number, number, number],
  lightBlue:  [219, 234, 254] as [number, number, number],
  white:      [255, 255, 255] as [number, number, number],
  slate:      [100, 116, 139] as [number, number, number],
  slateLight: [241, 245, 249] as [number, number, number],
  border:     [226, 232, 240] as [number, number, number],
  bodyText:   [30, 41, 59]   as [number, number, number],
  green:      [21, 128, 61]  as [number, number, number],
  greenLight: [240, 253, 244] as [number, number, number],
  red:        [185, 28, 28]  as [number, number, number],
  redLight:   [254, 242, 242] as [number, number, number],
};

// ─── Public API ───────────────────────────────────────────────────────────────

export function exportToPdf(
  project: Project,
  result: SizingResult,
  scenarioName: string,
): void {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  buildCoverPage(doc, project, result, scenarioName);
  buildBomSection(doc, result);
  buildComputeSection(doc, result);
  buildStorageSection(doc, result);
  buildNetworkSection(doc, result);
  buildMethodologySection(doc, project, result, scenarioName);

  addPageFooters(doc, project, scenarioName);

  const safeClient   = project.clientName.replace(/[^a-zA-Z0-9_-]/g, '_');
  const safeScenario = scenarioName.replace(/[^a-zA-Z0-9_-]/g, '_');
  const dateStr      = new Date().toISOString().slice(0, 10);
  doc.save(`EEN_Sizing_${safeClient}_${safeScenario}_${dateStr}.pdf`);
}

// ─── Cover Page ───────────────────────────────────────────────────────────────

function buildCoverPage(
  doc: jsPDF,
  project: Project,
  result: SizingResult,
  scenarioName: string,
): void {
  const W = doc.internal.pageSize.getWidth();

  // Dark header band
  doc.setFillColor(...C.navy);
  doc.rect(0, 0, W, 55, 'F');

  // Brand label
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...C.lightBlue);
  doc.text('EEN GROUP', 18, 18);

  // Tool name
  doc.setFontSize(22);
  doc.setTextColor(...C.white);
  doc.text('VM Infrastructure Sizing Report', 18, 32);

  // Scenario pill
  doc.setFillColor(...C.blue);
  const pillX = 18;
  const pillY = 40;
  const pillW = doc.getTextWidth(scenarioName) + 14;
  doc.roundedRect(pillX, pillY, pillW, 8, 2, 2, 'F');
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.white);
  doc.text(scenarioName, pillX + 7, pillY + 5.5);

  // Meta block
  const mx = 18;
  let my = 72;
  const rowH = 8;

  const meta: [string, string][] = [
    ['Project',       project.name],
    ['Client',        project.clientName],
    ['Generated',     new Date(result.generatedAt).toLocaleString()],
    ['Scenario',      scenarioName],
    ['BOM line items', String(result.bom.length)],
  ];

  doc.setFontSize(10);
  for (const [label, value] of meta) {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...C.slate);
    doc.text(label, mx, my);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.bodyText);
    doc.text(value, mx + 48, my);
    my += rowH;
  }

  // Divider
  doc.setDrawColor(...C.border);
  doc.line(mx, my + 2, W - mx, my + 2);
  my += 10;

  // Headline metrics grid
  const primaryCompute = result.compute.filter(r => r.site === 'primary');
  const drCompute      = result.compute.filter(r => r.site === 'dr');
  const primaryStorage = result.storage.find(r => r.site === 'primary');
  const primaryNetwork = result.network.find(r => r.site === 'primary');

  const totalPrimaryHosts = primaryCompute.reduce((s, r) => s + r.hostCountWithHA, 0);
  const totalDrHosts      = drCompute.reduce((s, r) => s + r.hostCountWithHA, 0);
  const totalPrimaryRawSan = Math.ceil(primaryStorage?.rawSanGb ?? 0);
  const uplink            = primaryNetwork?.recommendedUplinkGbps ?? 0;

  const tiles: { label: string; value: string; unit: string }[] = [
    { label: 'Primary Hosts',   value: String(totalPrimaryHosts),    unit: 'hosts' },
    { label: 'DR Hosts',        value: String(totalDrHosts),         unit: 'hosts' },
    { label: 'Primary SAN Raw', value: String(totalPrimaryRawSan),   unit: 'GB'    },
    { label: 'Uplink',          value: String(uplink),               unit: 'Gbps'  },
    { label: 'BOM Lines',       value: String(result.bom.length),    unit: 'items' },
  ];

  const tileW = 44;
  const tileH = 22;
  const tileGap = 6;
  let tx = mx;

  for (const tile of tiles) {
    doc.setFillColor(...C.slateLight);
    doc.setDrawColor(...C.border);
    doc.roundedRect(tx, my, tileW, tileH, 3, 3, 'FD');

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.slate);
    doc.text(tile.label, tx + tileW / 2, my + 6, { align: 'center' });

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...C.blue);
    doc.text(tile.value, tx + tileW / 2, my + 14, { align: 'center' });

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.slate);
    doc.text(tile.unit, tx + tileW / 2, my + 20, { align: 'center' });

    tx += tileW + tileGap;
  }
}

// ─── BOM Section ──────────────────────────────────────────────────────────────

function buildBomSection(doc: jsPDF, result: SizingResult): void {
  doc.addPage();
  addSectionHeader(doc, 'Bill of Materials');

  const head = [['Category', 'Site', 'Environment', 'Tier', 'Description', 'Qty', 'Unit', 'Spec Notes']];
  const body = result.bom.map(item => [
    item.category,
    item.site,
    item.environment,
    item.tierName ?? '',
    item.description,
    String(item.quantity),
    item.unit,
    item.specNotes,
  ]);

  autoTable(doc, {
    startY: 30,
    head,
    body,
    theme: 'grid',
    headStyles: {
      fillColor: C.blue,
      textColor: C.white,
      fontStyle: 'bold',
      fontSize: 8,
    },
    bodyStyles: {
      fontSize: 7,
      textColor: C.bodyText,
    },
    alternateRowStyles: {
      fillColor: C.slateLight,
    },
    columnStyles: {
      0: { cellWidth: 22 },
      1: { cellWidth: 16 },
      2: { cellWidth: 22 },
      3: { cellWidth: 24 },
      4: { cellWidth: 'auto' },
      5: { cellWidth: 10, halign: 'right' },
      6: { cellWidth: 14 },
      7: { cellWidth: 44 },
    },
    margin: { left: 12, right: 12 },
  });
}

// ─── Compute Section ──────────────────────────────────────────────────────────

function buildComputeSection(doc: jsPDF, result: SizingResult): void {
  doc.addPage();
  addSectionHeader(doc, 'Compute Summary');

  const head = [[
    'Site', 'Environment', 'Tier',
    'Raw vCPU', 'Raw Mem GB',
    'Phys Cores', 'Phys Mem GB',
    'Hosts (no HA)', 'Hosts (HA)',
    'Min GHz', 'ULL VMs?',
  ]];
  const body = result.compute.map((r: ComputeResult) => [
    r.site,
    r.environment,
    r.tierName ?? '',
    String(r.rawVcpuRequired),
    String(r.rawMemoryGbRequired),
    String(r.physicalCoresRequired),
    String(r.physicalMemoryGbRequired),
    String(r.hostCountWithoutHA),
    String(r.hostCountWithHA),
    String(r.recommendedHostSpec.minCpuGhz),
    r.hasUltraLowLatencyVms ? 'YES ⚠' : 'No',
  ]);

  autoTable(doc, {
    startY: 30,
    head,
    body,
    theme: 'grid',
    headStyles: { fillColor: C.blue, textColor: C.white, fontStyle: 'bold', fontSize: 8 },
    bodyStyles: { fontSize: 8, textColor: C.bodyText },
    alternateRowStyles: { fillColor: C.slateLight },
    margin: { left: 12, right: 12 },
  });

  addNotesTable(doc, result.compute.map((r: ComputeResult) => ({
    label: `${r.site} / ${r.environment}${r.tierName ? ` / ${r.tierName}` : ''}`,
    notes: r.notes,
  })));
}

// ─── Storage Section ──────────────────────────────────────────────────────────

function buildStorageSection(doc: jsPDF, result: SizingResult): void {
  doc.addPage();
  addSectionHeader(doc, 'Storage Summary');

  const head = [[
    'Site',
    'Usable SAN GB', 'Usable NAS GB', 'Usable Local GB',
    'Raw SAN GB', 'Raw NAS GB', 'Raw Local GB',
    'Total SAN IOPS', 'Effective IOPS',
  ]];
  const body = result.storage.map((r: StorageResult) => [
    r.site,
    String(r.usableSanGb),
    String(r.usableNasGb),
    String(r.usableLocalGb),
    String(r.rawSanGb),
    String(r.rawNasGb),
    String(r.rawLocalGb),
    String(r.totalSanIops),
    String(r.effectiveIopsRequired),
  ]);

  autoTable(doc, {
    startY: 30,
    head,
    body,
    theme: 'grid',
    headStyles: { fillColor: C.blue, textColor: C.white, fontStyle: 'bold', fontSize: 8 },
    bodyStyles: { fontSize: 8, textColor: C.bodyText },
    alternateRowStyles: { fillColor: C.slateLight },
    margin: { left: 12, right: 12 },
  });

  addNotesTable(doc, result.storage.map((r: StorageResult) => ({
    label: r.site,
    notes: r.notes,
  })));
}

// ─── Network Section ──────────────────────────────────────────────────────────

function buildNetworkSection(doc: jsPDF, result: SizingResult): void {
  doc.addPage();
  addSectionHeader(doc, 'Network Summary');

  const head = [[
    'Site',
    'Total VM BW (Mbps)',
    'East-West (Mbps)',
    'North-South (Mbps)',
    'Uplink (Gbps)',
    'Access Ports',
    'VLANs',
  ]];
  const body = result.network.map((r: NetworkResult) => [
    r.site,
    String(r.totalVmBandwidthMbps),
    String(r.eastWestBandwidthMbps),
    String(r.northSouthBandwidthMbps),
    String(r.recommendedUplinkGbps),
    String(r.recommendedAccessPortCount),
    String(r.requiredVlanCount),
  ]);

  autoTable(doc, {
    startY: 30,
    head,
    body,
    theme: 'grid',
    headStyles: { fillColor: C.blue, textColor: C.white, fontStyle: 'bold', fontSize: 8 },
    bodyStyles: { fontSize: 8, textColor: C.bodyText },
    alternateRowStyles: { fillColor: C.slateLight },
    margin: { left: 12, right: 12 },
  });

  addNotesTable(doc, result.network.map((r: NetworkResult) => ({
    label: r.site,
    notes: r.notes,
  })));
}

// ─── Methodology Section ──────────────────────────────────────────────────────

function buildMethodologySection(
  doc: jsPDF,
  project: Project,
  result: SizingResult,
  scenarioName: string,
): void {
  doc.addPage();
  addSectionHeader(doc, `Methodology Notes — ${scenarioName}`);

  const scenario = project.scenarios.find(s => s.id === result.scenarioId)
    ?? project.scenarios.find(s => s.isBaseline)
    ?? project.scenarios[0];

  const cfg: SizingConfig = scenario.sizingConfig;

  // Sizing parameters table
  const paramRows: [string, string][] = [
    ['vCPU Overcommit Ratio',       String(cfg.vcpuOvercommitRatio)],
    ['Memory Overcommit Ratio',     String(cfg.memoryOvercommitRatio)],
    ['Platform Overhead %',         String(cfg.platformOverheadPct)],
    ['HA Model',                    cfg.haModel],
    ['Redundancy Model',            cfg.redundancyModel],
    ['DR Tier',                     cfg.drTier],
    ['DR Compute Fraction %',       String(cfg.drComputeFractionPct)],
    ['Storage Architecture',        cfg.storageArchitecture],
    ['Storage Usable-to-Raw Ratio', String(cfg.storageUsableToRawRatio)],
    ['Storage Growth Buffer %',     String(cfg.storageGrowthBufferPct)],
    ['RAID Write Penalty Factor',   String(cfg.raidWritePenaltyFactor)],
  ];

  autoTable(doc, {
    startY: 30,
    head: [['Parameter', 'Value']],
    body: paramRows,
    theme: 'grid',
    headStyles: { fillColor: C.navy, textColor: C.white, fontStyle: 'bold', fontSize: 8 },
    bodyStyles: { fontSize: 8, textColor: C.bodyText },
    alternateRowStyles: { fillColor: C.slateLight },
    columnStyles: {
      0: { cellWidth: 80, fontStyle: 'bold' },
      1: { cellWidth: 60 },
    },
    margin: { left: 12, right: 12 },
  });

  // Get final Y after the parameters table
  const finalY = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY ?? 80;

  // BOM methodology notes
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.navy);
  doc.text('BOM Methodology Notes', 12, finalY + 10);

  autoTable(doc, {
    startY: finalY + 14,
    head: [['Category', 'Description', 'Methodology Note']],
    body: result.bom.map(item => [item.category, item.description, item.methodologyNote]),
    theme: 'grid',
    headStyles: { fillColor: C.navy, textColor: C.white, fontStyle: 'bold', fontSize: 8 },
    bodyStyles: { fontSize: 7, textColor: C.bodyText },
    alternateRowStyles: { fillColor: C.slateLight },
    columnStyles: {
      0: { cellWidth: 28 },
      1: { cellWidth: 60 },
      2: { cellWidth: 'auto' },
    },
    margin: { left: 12, right: 12 },
  });

  // Footer disclaimer
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  doc.setFontSize(7);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(...C.slate);
  const disclaimer =
    'This report is generated by the EEN VM Infrastructure Sizing Tool. ' +
    'All sizing parameters are applied once. ' +
    'vCPU overcommit ratio and platform overhead are independent factors and are never compounded. ' +
    'DR storage is always fully replicated regardless of DR tier. ' +
    'This document is confidential and intended solely for the named client.';
  const lines = doc.splitTextToSize(disclaimer, W - 24);
  doc.text(lines, 12, H - 14);
}

// ─── Shared helpers ───────────────────────────────────────────────────────────

function addSectionHeader(doc: jsPDF, title: string): void {
  const W = doc.internal.pageSize.getWidth();

  doc.setFillColor(...C.navy);
  doc.rect(0, 0, W, 18, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...C.white);
  doc.text(title, 12, 12);
}

function addNotesTable(
  doc: jsPDF,
  rows: { label: string; notes: string[] }[],
): void {
  const filtered = rows.filter(r => r.notes.length > 0);
  if (filtered.length === 0) return;

  const finalY = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY ?? 30;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.navy);
  doc.text('Methodology Notes', 12, finalY + 8);

  autoTable(doc, {
    startY: finalY + 12,
    head: [['Domain', 'Notes']],
    body: filtered.map(r => [r.label, r.notes.join('\n')]),
    theme: 'grid',
    headStyles: { fillColor: C.navy, textColor: C.white, fontStyle: 'bold', fontSize: 8 },
    bodyStyles: { fontSize: 7, textColor: C.bodyText },
    alternateRowStyles: { fillColor: C.slateLight },
    columnStyles: {
      0: { cellWidth: 40 },
      1: { cellWidth: 'auto' },
    },
    margin: { left: 12, right: 12 },
  });
}

function addPageFooters(doc: jsPDF, project: Project, scenarioName: string): void {
  const totalPages = doc.getNumberOfPages();
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    doc.setDrawColor(...C.border);
    doc.line(12, H - 10, W - 12, H - 10);

    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.slate);

    doc.text(`EEN VM Sizing Tool · ${project.clientName} · ${scenarioName}`, 12, H - 6);
    doc.text(`Page ${i} of ${totalPages}`, W - 12, H - 6, { align: 'right' });
  }
}
